/*
    The useAppDispatch hook wraps useDispatch
    to provide a custom dispatch function that handles async actions,
    returning the unwrapped result or throwing an error.
*/

import { useMemo } from "react";
// eslint-disable-next-line no-restricted-imports
import { useDispatch } from "react-redux";
import { createCustomDispatch, createCustomDispatchReturnErrors } from "helpers/customDispatch";

/**
 * Plain Redux `dispatch` (not wrapped). Use with `createCustomDispatch` in the component.
 * Do not compose with `useAppDispatch` — that would double-unwrap thunks (e.g. `unwrapResult(null)`).
 */
export const useReduxDispatch = () => useDispatch();

const useAppDispatch = () => {
  const dispatch = useDispatch();

  // createCustomDispatch returns a new function each call — memoize so useEffect/useCallback deps stay stable
  return useMemo(() => createCustomDispatch(dispatch), [dispatch]);
};

// Use this hook for components that don't use try-catch with dispatch
// and want errors returned as objects instead of thrown
export const useAppDispatchReturnErrors = () => {
  const dispatch = useDispatch();

  return useMemo(() => createCustomDispatchReturnErrors(dispatch), [dispatch]);
};

export default useAppDispatch;
